/**
 * The one live player trail for the run — the breadcrumb queue the dog follows (design D1
 * of `robot-dog-follows-player`).
 *
 * A plain mutable module object, deliberately outside React and the store, exactly like
 * `src/input/inputState.ts`: it is written every frame from `Player.tsx`'s frame loop, and
 * per-frame data must never go through a render (architectural rule 3). `useDogFollow.ts`
 * reads and prunes the same instance.
 */

import { clearTrail, createTrail, pushTrailPoint, type Trail } from '../game/trail';
import type { Vec3 } from '../game/profile/types';

const trail: Trail = createTrail();

/**
 * Scratch point, mutated in place and reused every call: `pushTrailPoint` copies whatever
 * it is given onto the queue, so nothing here needs to allocate a new object per frame.
 */
const scratch: Vec3 = { x: 0, y: 0, z: 0 };

/** The one live trail for the run. `useDogFollow.ts` prunes and steers off this instance. */
export function getPlayerTrail(): Trail {
  return trail;
}

/**
 * Drops a trail point at the player's feet — the same convention `Spawn.position` uses —
 * subject to `trail.ts`'s own `TRAIL_SPACING` rule. Cheap to call every frame: most calls
 * are no-ops until the player has actually travelled far enough.
 */
export function pushPlayerPosition(x: number, y: number, z: number): void {
  scratch.x = x;
  scratch.y = y;
  scratch.z = z;
  pushTrailPoint(trail, scratch);
}

/**
 * Empties the trail. Called when `Player` mounts: `Player` is keyed on
 * `${level.id}:${arriveAt}` in `App.tsx`, so a mount is exactly a new level or a new
 * arrival, and a route left over from the previous level must never survive into this one.
 */
export function resetPlayerTrail(): void {
  clearTrail(trail);
}
